import { useTranslation } from "@warpme/i18n"
import { type FC, useEffect, useRef } from "react"
import { Outlet, useLocation, useNavigate, useNavigationType } from "react-router"

import { toast } from "@/components/ui/use-toast"
import { getCurrentFirebaseAuthLanguageCode } from "@/lib/current-language"
import { auth } from "@/lib/firebase"
import { isFlashToastState, toFlashToastOptions } from "@/lib/flash-toast"
import { Footer } from "./components/footer"
import { Header } from "./components/Header"

export const GlobalLayout: FC = () => {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const hasMountedRef = useRef(false)
  const handledLocationKeysRef = useRef(new Set<string>())

  useEffect(() => {
    auth.setLanguageCode(getCurrentFirebaseAuthLanguageCode(i18n))
  }, [i18n, i18n.language, i18n.resolvedLanguage])

  useEffect(() => {
    if (!isFlashToastState(location.state) || !location.state.flashToast) {
      return
    }

    if (handledLocationKeysRef.current.has(location.key)) {
      return
    }

    handledLocationKeysRef.current.add(location.key)

    const options = toFlashToastOptions(location.state.flashToast, t)
    toast[options.kind](options)

    const nextState = { ...location.state }
    delete nextState.flashToast

    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: Object.keys(nextState).length > 0 ? nextState : null,
    })
  }, [location.hash, location.key, location.pathname, location.search, location.state, navigate, t])

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }

    if (navigationType === "POP" || location.hash) {
      return
    }

    window.scrollTo(0, 0)
  }, [location.hash, location.key, navigationType])

  return (
    <>
      <Header className="z-10" />
      <main className="w-full max-w-screen-md mx-auto mt-3 pb-6">
        <Outlet />
      </main>
      <Footer />
    </>
  )
}
